import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  adminApi,
  AdminUser,
  AdminUserCreatePayload,
  AdminUserUpdatePayload,
  ViewMode,
} from '../api/admin';
import AdminTabsNav from '../components/AdminTabsNav';
import Modal from '../components/Modal';
import { useIsMobileViewport } from '../utils/useIsMobileViewport';
import './AdminConfig.css';
import './AdminUsers.css';

type ApiError = { response?: { data?: { detail?: string } } };

const extractError = (e: unknown, fallback: string): string => {
  const err = e as ApiError;
  return err.response?.data?.detail || fallback;
};

type EditorMode = 'create' | 'edit';

interface EditorState {
  mode: EditorMode;
  user?: AdminUser;
}

interface FormState {
  email: string;
  full_name: string;
  password: string;
  is_active: boolean;
  is_admin: boolean;
  view_mode: ViewMode;
}

const emptyForm = (): FormState => ({
  email: '',
  full_name: '',
  password: '',
  is_active: true,
  is_admin: false,
  view_mode: 'simplified',
});

const fromUser = (u: AdminUser): FormState => ({
  email: u.email,
  full_name: u.full_name ?? '',
  password: '',
  is_active: u.is_active,
  is_admin: u.is_admin,
  view_mode: u.view_mode,
});


const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const isMobile = useIsMobileViewport();

  const load = async () => {
    setUsers(await adminApi.users.list());
  };

  useEffect(() => {
    if (!currentUser?.is_admin) return;
    (async () => {
      try {
        await load();
      } catch (e) {
        setError(extractError(e, 'Nu s-au putut încărca utilizatorii'));
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q)
    );
  }, [users, query]);

  // Mirrors the backend lockout guards: can't delete yourself, can't
  // delete the last active admin. Disabling client-side avoids a
  // pointless 400 round-trip and keeps the destructive action quiet.
  const canDelete = (u: AdminUser): boolean => {
    if (!currentUser) return false;
    if (u.id === currentUser.id) return false;
    if (u.is_admin) {
      const activeAdmins = users.filter((x) => x.is_admin && x.is_active && x.id !== u.id).length;
      if (activeAdmins < 1) return false;
    }
    return true;
  };

  const handleDelete = async (u: AdminUser) => {
    const ok = window.confirm(
      `Ștergeți utilizatorul „${u.email}”?\n\n` +
        'Această acțiune este definitivă și va șterge toate familiile, membrii,\n' +
        'documentele și datele asociate. Nu poate fi anulată.'
    );
    if (!ok) return;
    setError('');
    setInfo('');
    try {
      await adminApi.users.remove(u.id);
      setInfo(`Utilizator șters: ${u.email}`);
      await load();
    } catch (e) {
      setError(extractError(e, 'Eroare la ștergerea utilizatorului'));
    }
  };

  if (!currentUser?.is_admin) {
    return (
      <div className="page-container">
        <p className="alert alert-error">Acces interzis.</p>
        <Link to="/dashboard">Înapoi</Link>
      </div>
    );
  }

  return (
    <div className="page-container admin-page">
      <nav className="breadcrumbs admin-breadcrumbs">
        <Link to="/dashboard">Dashboard</Link>
        <span className="admin-bc-sep">/</span>
        <span>Administrare</span>
        <span className="admin-bc-sep">/</span>
        <span>Utilizatori</span>
      </nav>

      <AdminTabsNav />

      <header className="admin-hero">
        <div className="admin-hero-text">
          <h1 className="page-title admin-hero-title">Utilizatori</h1>
          <p className="page-subtitle admin-hero-sub">
            Gestionați conturile platformei: creați utilizatori noi, schimbați rolurile, resetați parola sau ștergeți conturi.
          </p>
        </div>
        <div className="admin-hero-actions">
          <button type="button" className="btn btn-primary" onClick={() => setEditor({ mode: 'create' })}>
            + Utilizator nou
          </button>
        </div>
      </header>

      {error && (
        <div className="admin-flash admin-flash--error" role="alert">
          <span className="admin-flash__text">{error}</span>
        </div>
      )}
      {info && (
        <div className="admin-flash admin-flash--success" role="status">
          <span className="admin-flash__text">{info}</span>
        </div>
      )}

      <details
        className="admin-toolbar-panel admin-toolbar-collapsible"
        open={!isMobile}
        aria-label="Filtrare utilizatori"
      >
        <summary className="admin-toolbar-summary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="7" y1="12" x2="17" y2="12" />
            <line x1="10" y1="18" x2="14" y2="18" />
          </svg>
          <span>Filtre {query ? <em className="admin-toolbar-summary__active">(active)</em> : null}</span>
          <svg className="admin-toolbar-summary__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </summary>
        <div className="admin-toolbar-panel__row">
          <div className="admin-toolbar-search-block">
            <span className="admin-toolbar-filters-label" id="admin-users-search-label">
              Caută
            </span>
            <div className="admin-search-wrap">
              <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                className="input admin-search"
                placeholder="Email sau nume…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-labelledby="admin-users-search-label"
              />
            </div>
          </div>
        </div>
      </details>

      {users.length > 0 && (
        <div className="admin-stats" aria-live="polite">
          <span className="admin-stats__badge">
            <strong>{users.length}</strong> utilizatori în total
          </span>
          {filtered.length !== users.length && (
            <span className="admin-stats__badge admin-stats__badge--muted">
              <strong>{filtered.length}</strong> după filtru
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="admin-skeleton-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card admin-skeleton-card" />
          ))}
        </div>
      ) : (
        <div className="admin-users-card">
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th className="admin-users-th-user">Utilizator</th>
                  <th>Rol</th>
                  <th>Stare</th>
                  <th>Vedere</th>
                  <th className="admin-users-th-num">Familii</th>
                  <th>E2EE</th>
                  <th>Creat</th>
                  <th className="admin-users-th-actions" aria-label="Acțiuni" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isSelf={u.id === currentUser?.id}
                    canDelete={canDelete(u)}
                    menuOpen={openMenuId === u.id}
                    onOpenMenu={() => setOpenMenuId(u.id)}
                    onCloseMenu={() => setOpenMenuId(null)}
                    onEdit={() => {
                      setOpenMenuId(null);
                      setEditor({ mode: 'edit', user: u });
                    }}
                    onResetPassword={() => {
                      setOpenMenuId(null);
                      setResetUser(u);
                    }}
                    onDelete={() => {
                      setOpenMenuId(null);
                      void handleDelete(u);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="card admin-empty">
          <p>Niciun utilizator nu se potrivește filtrului.</p>
        </div>
      )}

      <UserEditorModal
        state={editor}
        currentUserId={currentUser?.id ?? -1}
        activeAdminCount={users.filter((u) => u.is_admin && u.is_active).length}
        onClose={() => setEditor(null)}
        onSaved={async (msg) => {
          setEditor(null);
          setInfo(msg);
          setError('');
          await load();
        }}
        onError={(msg) => setError(msg)}
      />

      <PasswordResetModal
        user={resetUser}
        onClose={() => setResetUser(null)}
        onSaved={async (msg) => {
          setResetUser(null);
          setInfo(msg);
          setError('');
        }}
        onError={(msg) => setError(msg)}
      />
    </div>
  );
};


// ---------------------------------------------------------------------------
// Single row + per-row context menu.
//
// The dropdown is rendered through a React portal with `position: fixed`
// (see RowActionsMenu) so the surrounding table card's `overflow: hidden`
// can't clip it — the table needs `overflow: hidden` for its rounded
// corners and `overflow-x: auto` for horizontal scroll on narrow viewports,
// neither of which we want to relax.
// ---------------------------------------------------------------------------

interface UserRowProps {
  user: AdminUser;
  isSelf: boolean;
  canDelete: boolean;
  menuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onEdit: () => void;
  onResetPassword: () => void;
  onDelete: () => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  isSelf,
  canDelete,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
  onEdit,
  onResetPassword,
  onDelete,
}) => {
  const initials = (user.full_name || user.email)
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <tr className={isSelf ? 'admin-users-row--self' : undefined}>
      <td>
        <div className="admin-users-user-cell">
          <span className="admin-users-avatar" aria-hidden>{initials}</span>
          <div className="admin-users-user-text">
            <span className="admin-users-user-name">
              {user.full_name || <span className="admin-users-muted">—</span>}
            </span>
            <span className="admin-users-user-email">
              {user.email}
              {isSelf && <span className="admin-users-self-tag">tu</span>}
            </span>
          </div>
        </div>
      </td>
      <td>
        {user.is_admin ? (
          <span className="admin-pill admin-pill--admin">Admin</span>
        ) : (
          <span className="admin-pill admin-pill--user">Utilizator</span>
        )}
      </td>
      <td>
        {user.is_active ? (
          <span className="admin-pill admin-pill--active">
            <span className="admin-pill-dot" /> Activ
          </span>
        ) : (
          <span className="admin-pill admin-pill--inactive">
            <span className="admin-pill-dot" /> Inactiv
          </span>
        )}
      </td>
      <td className="admin-users-muted-cell">
        {user.view_mode === 'advanced' ? 'Avansat' : 'Simplificat'}
      </td>
      <td className="admin-users-th-num admin-users-muted-cell">{user.family_count}</td>
      <td>
        {user.has_crypto_key ? (
          <span className="admin-users-e2ee-yes" title="Cheie E2EE configurată" aria-label="Cheie E2EE configurată">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
        ) : (
          <span className="admin-users-muted" title="Fără cheie E2EE">—</span>
        )}
      </td>
      <td className="admin-users-muted-cell">
        {new Date(user.created_at).toLocaleDateString('ro-RO')}
      </td>
      <td className="admin-users-cell-actions">
        <RowActionsMenu
          open={menuOpen}
          onOpen={onOpenMenu}
          onClose={onCloseMenu}
          isSelf={isSelf}
          canDelete={canDelete}
          onEdit={onEdit}
          onResetPassword={onResetPassword}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
};


// ---------------------------------------------------------------------------
// Portal-rendered row context menu.
//
// Why a portal:
//   - The actions cell lives inside <td>, inside the .admin-users-table-wrap
//     scroll container, inside the .admin-users-card with rounded corners.
//     Both `overflow: hidden` and `overflow-x: auto` clip an absolutely
//     positioned dropdown. Rendering to document.body with `position:
//     fixed` makes the dropdown immune to ancestor overflow.
//
// Positioning:
//   - Anchored to the trigger button's bottom-right corner.
//   - On open + on scroll/resize we recompute via getBoundingClientRect.
//   - If the menu would overflow the viewport bottom, we flip above the
//     trigger; if it would overflow the right edge, we shift left.
// ---------------------------------------------------------------------------

interface RowActionsMenuProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  isSelf: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onResetPassword: () => void;
  onDelete: () => void;
}

const MENU_WIDTH = 208;
const MENU_HEIGHT_EST = 168; // approximate; only used for flip decision

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  open,
  onOpen,
  onClose,
  isSelf,
  canDelete,
  onEdit,
  onResetPassword,
  onDelete,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const recompute = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Default: drop below, align right edge to trigger right edge.
    let left = rect.right - MENU_WIDTH;
    let top = rect.bottom + 6;

    // Flip up if no room below.
    if (top + MENU_HEIGHT_EST > viewportH - 8) {
      top = rect.top - MENU_HEIGHT_EST - 6;
    }
    // Keep on screen horizontally.
    if (left < 8) left = 8;
    if (left + MENU_WIDTH > viewportW - 8) left = viewportW - MENU_WIDTH - 8;

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    recompute();
  }, [open, recompute]);

  // Outside-click + Escape close the menu. Scroll/resize while open
  // recompute position so the dropdown tracks the trigger as the user
  // scrolls (e.g. table scroll, page scroll, viewport resize).
  useEffect(() => {
    if (!open) return;

    const handleDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    const handleScroll = () => recompute();
    const handleResize = () => recompute();

    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    // capture: true so nested scroll containers also trigger reposition.
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, onClose, recompute]);

  const deleteDisabledReason = !canDelete
    ? isSelf
      ? 'Nu îți poți șterge propriul cont'
      : 'Nu se poate șterge ultimul administrator'
    : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`admin-users-menu-btn${open ? ' admin-users-menu-btn--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Acțiuni utilizator"
        onClick={() => (open ? onClose() : onOpen())}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      {open && pos && createPortal(
        <div
          ref={popRef}
          className="admin-users-menu-pop"
          role="menu"
          style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
        >
          <button
            type="button"
            role="menuitem"
            className="admin-users-menu-item"
            onClick={onEdit}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Editează
          </button>
          <button
            type="button"
            role="menuitem"
            className="admin-users-menu-item"
            onClick={onResetPassword}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M3 22v-6h6" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
            Resetează parola
          </button>
          <div className="admin-users-menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="admin-users-menu-item admin-users-menu-item--danger"
            onClick={onDelete}
            disabled={!canDelete}
            title={deleteDisabledReason}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
            Șterge
          </button>
        </div>,
        document.body
      )}
    </>
  );
};


// ---------------------------------------------------------------------------
// Create / Edit modal — built on the shared <Modal> component so it has
// the same header, fixed footer, scroll behavior, focus management and
// backdrop animation as every other modal in the app.
// ---------------------------------------------------------------------------

interface UserEditorModalProps {
  state: EditorState | null;
  currentUserId: number;
  activeAdminCount: number;
  onClose: () => void;
  onSaved: (msg: string) => void | Promise<void>;
  onError: (msg: string) => void;
}

const UserEditorModal: React.FC<UserEditorModalProps> = ({
  state,
  currentUserId,
  activeAdminCount,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = 'admin-user-form';
  const isEdit = state?.mode === 'edit';
  const editingSelf = isEdit && state?.user?.id === currentUserId;

  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  // Reset the form whenever the modal opens for a different user / mode.
  useEffect(() => {
    if (!state) return;
    setForm(state.user ? fromUser(state.user) : emptyForm());
    setLocalError('');
    setSaving(false);
  }, [state]);

  // Mirrors the backend lockout guard so the field reads as disabled
  // before submit. If THIS admin is the only remaining active admin,
  // neither demote nor deactivate is allowed.
  const wouldBeLastAdmin = useMemo(() => {
    if (!isEdit || !state?.user || !state.user.is_admin) return false;
    const stillActiveAdmins =
      activeAdminCount - (state.user.is_admin && state.user.is_active ? 1 : 0);
    return stillActiveAdmins < 1;
  }, [isEdit, state, activeAdminCount]);

  const adminCheckboxDisabled = isEdit && (editingSelf || wouldBeLastAdmin);
  const activeCheckboxDisabled = isEdit && (editingSelf || wouldBeLastAdmin) && form.is_admin;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!state) return;
    setLocalError('');
    if (!isEdit) {
      if (!form.email.trim()) {
        setLocalError('Email obligatoriu');
        return;
      }
      if (!form.password) {
        setLocalError('Parola este obligatorie pentru un utilizator nou');
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit && state.user) {
        const payload: AdminUserUpdatePayload = {
          full_name: form.full_name.trim() || null,
          is_active: form.is_active,
          is_admin: form.is_admin,
          view_mode: form.view_mode,
        };
        await adminApi.users.update(state.user.id, payload);
        await onSaved(`Utilizator actualizat: ${state.user.email}`);
      } else {
        const payload: AdminUserCreatePayload = {
          email: form.email.trim().toLowerCase(),
          password: form.password,
          full_name: form.full_name.trim() || null,
          is_active: form.is_active,
          is_admin: form.is_admin,
          view_mode: form.view_mode,
        };
        const created = await adminApi.users.create(payload);
        await onSaved(`Utilizator creat: ${created.email}`);
      }
    } catch (e) {
      const msg = extractError(e, 'Eroare la salvare');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      onClose={onClose}
      title={isEdit ? `Editează utilizator` : 'Utilizator nou'}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : isEdit ? 'Salvează' : 'Creează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="admin-user-email">
            Email
          </label>
          <input
            id="admin-user-email"
            type="email"
            className="form-input"
            required
            disabled={isEdit}
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            autoComplete="off"
          />
          {isEdit && (
            <p className="form-hint">Emailul nu poate fi modificat din această interfață.</p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="admin-user-name">
            Nume complet
          </label>
          <input
            id="admin-user-name"
            type="text"
            className="form-input"
            value={form.full_name}
            onChange={(e) => set('full_name', e.target.value)}
            autoComplete="off"
          />
        </div>

        {!isEdit && (
          <div className="form-group">
            <label className="form-label" htmlFor="admin-user-password">
              Parolă inițială
            </label>
            <input
              id="admin-user-password"
              type="password"
              className="form-input"
              required
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              autoComplete="new-password"
            />
            <p className="form-hint">
              Utilizatorul își va configura propria cheie E2EE la prima autentificare.
            </p>
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="admin-user-view">
            Vedere implicită
          </label>
          <select
            id="admin-user-view"
            className="form-select"
            value={form.view_mode}
            onChange={(e) => set('view_mode', e.target.value as ViewMode)}
          >
            <option value="simplified">Simplificat</option>
            <option value="advanced">Avansat</option>
          </select>
        </div>

        <div className="form-group">
          <span className="form-label">Roluri și acces</span>
          <div className="admin-form-checks">
            <label className="admin-form-check">
              <input
                type="checkbox"
                checked={form.is_active}
                disabled={activeCheckboxDisabled}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              <span>Cont activ</span>
            </label>
            <label className="admin-form-check">
              <input
                type="checkbox"
                checked={form.is_admin}
                disabled={adminCheckboxDisabled}
                onChange={(e) => set('is_admin', e.target.checked)}
              />
              <span>Administrator</span>
            </label>
          </div>
          {(editingSelf || wouldBeLastAdmin) && isEdit && (
            <p className="form-hint">
              {editingSelf
                ? 'Nu îți poți modifica propriul rol sau dezactiva propriul cont aici.'
                : 'Trebuie să rămână cel puțin un administrator activ.'}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
};


// ---------------------------------------------------------------------------
// Password reset modal — same shared <Modal> shell.
// ---------------------------------------------------------------------------

interface PasswordResetModalProps {
  user: AdminUser | null;
  onClose: () => void;
  onSaved: (msg: string) => void | Promise<void>;
  onError: (msg: string) => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  user,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = 'admin-user-reset-form';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!user) return;
    setPassword('');
    setConfirm('');
    setLocalError('');
    setSaving(false);
  }, [user]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!user) return;
    setLocalError('');
    if (!password) {
      setLocalError('Parolă obligatorie');
      return;
    }
    if (password !== confirm) {
      setLocalError('Parolele nu coincid');
      return;
    }
    setSaving(true);
    try {
      await adminApi.users.resetPassword(user.id, password);
      await onSaved(`Parolă resetată pentru ${user.email}`);
    } catch (e) {
      const msg = extractError(e, 'Eroare la resetarea parolei');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title="Resetează parola"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-danger" disabled={saving}>
            {saving ? 'Se resetează…' : 'Resetează parola'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <p className="form-hint" style={{ marginBottom: 'var(--sp-4)' }}>
          Veți stabili o parolă nouă pentru <strong>{user?.email}</strong>.
          {user?.has_crypto_key && (
            <>
              {' '}
              <strong>Atenție:</strong> documentele criptate end-to-end ale acestui
              utilizator folosesc parola lor veche pentru deblocare. După resetare,
              accesul la acele documente va fi pierdut dacă utilizatorul nu își
              amintește parola anterioară.
            </>
          )}
        </p>

        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="admin-reset-password">
            Parolă nouă
          </label>
          <input
            id="admin-reset-password"
            type="password"
            className="form-input"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="admin-reset-confirm">
            Confirmare parolă
          </label>
          <input
            id="admin-reset-confirm"
            type="password"
            className="form-input"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </div>
      </form>
    </Modal>
  );
};


export default AdminUsers;
